import React, { useState, useEffect } from "react";
import { TrainerAPI } from "../../api/TrainerAPI";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  DollarSign,
  Receipt,
  Calendar,
  Timer,
  Upload,
  FolderOpen,
  Info,
  ChevronDown,
  ChevronRight,
  Download,
  Copy,
  Gift,
  Users,
  X,
  Loader2,
  CheckCircle2,
  Award,
  Star,
  Video,
  Coins,
  BadgeCheck,
  Search,
} from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

// Same convention as StudentClassesModal's STUDENT_STATUS_STYLES: a
// lowercased-status -> tailwind pill map, falling back to neutral gray.
const STUDENT_STATUS_STYLES = {
  active: "text-green-700 bg-green-100",
  hold: "text-red-700 bg-red-100",
  dropped: "text-red-700 bg-red-100",
  completed: "text-blue-700 bg-blue-100",
};

const getStudentStatusColor = (status) =>
  STUDENT_STATUS_STYLES[status?.toLowerCase()] || "text-gray-600 bg-gray-100";
  
const RaiseInvoiceModal = ({
  batches,
  isOpen,
  onClose,
  onSuccess,
  trainerEmail,
}) => {
  const [trainerProfile, setTrainerProfile] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [filteredBatches, setFilteredBatches] = useState(batches || []);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    pan: "",
    sessionType: "Training",
    courseName: "",
    numOfStudents: 0,
    numOfClasses: 0,
    batchNo: "",
    batchNumber2: "0",
    batchNumber3: "0",
    trainerLeaves: 0,
    totalAmount: "",
    // Bank Details split into individual fields (all mandatory)
    bankAccountHolderName: "",
    bankAccountNumber: "",
    bankIFSC: "",
    upiDetails: "",
    contactNumber: "",
    date: new Date().toISOString().split("T")[0],
    remark: "",
    batchTiming: "",
    sessionDate: new Date().toISOString().split("T")[0],
    sessionTime: "",
    paymentType: "",
    numClassesAsMOU: "0",
    costAsPerMOU: "0",
    attendedHours: "0",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Acknowledgment checkbox that gates the Submit button
  const [acknowledged, setAcknowledged] = useState(false);

  // Initialize filtered batches when batches prop changes
  useEffect(() => {
    setFilteredBatches(batches || []);
  }, [batches]);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".relative")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchTrainerProfile = async () => {
      try {
        const profile = await TrainerAPI.getTrainerProfile();
        setTrainerProfile(profile);

        // Set basic trainer info immediately
        setFormData((prev) => ({
          ...prev,
          name: profile.name || "",
          email: profile.email || trainerEmail || "",
          pan: profile.instructor_pan || "",
          // Bank Details are intentionally NOT prefilled from the profile —
          // the trainer must type Account Holder Name, Account Number, and
          // IFSC Code manually every time they raise an invoice.
          contactNumber: profile.contact || "",
          // UPI ID is also intentionally NOT prefilled from the profile —
          // the trainer must type it in manually every time.
        }));
      } catch (err) {
        console.error("Failed to load trainer profile", err);
      }
    };

    if (isOpen) {
      fetchTrainerProfile();
    }
  }, [isOpen, trainerEmail]);

  // Update form data when batch is selected (for Training sessions only)
  useEffect(() => {
    if (selectedBatch && formData.sessionType === "Training") {
      const instructor =
        selectedBatch.instructor?.find((inst) => inst.email === trainerEmail) ||
        {};

      // NEW: Handle linked batches
      const linkedBatches = selectedBatch.linkedBatches || [];
      const batchNumber2 = linkedBatches.length > 0 ? linkedBatches[0] : "0";
      const batchNumber3 = linkedBatches.length > 1 ? linkedBatches[1] : "0";

      setFormData((prev) => ({
        ...prev,
        courseName: selectedBatch.courseName || "",
        numOfStudents: selectedBatch.students?.length || 0,
        numOfClasses:
          instructor.sessionsConducted || selectedBatch.numSessions || 0,
        batchNo: selectedBatch.batch_no || "",
        // NEW: Add linked batches
        batchNumber2: batchNumber2,
        batchNumber3: batchNumber3,
        trainerLeaves:
          instructor.trainerLeaves || selectedBatch.trainerLeaves || 0,
        batchTiming: selectedBatch.batchTiming || "",
        numClassesAsMOU: selectedBatch.mouHours || "0",
        costAsPerMOU: selectedBatch.mouAmount || "0",
        attendedHours: selectedBatch.attendedHours || "0",
      }));
    }
  }, [selectedBatch, formData.sessionType, trainerEmail]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Reset batch-related fields when session type changes
    if (name === "sessionType") {
      if (value !== "Training") {
        setSelectedBatch(null);
        setSearchKeyword("");
        setFilteredBatches(batches || []);
        setFormData((prev) => ({
          ...prev,
          batchNo: "0", // Default batch number for non-training sessions
          numOfClasses: 0,
          trainerLeaves: 0,
          batchTiming: "",
          courseName: "",
        }));
      }
    }
  };
  // Convert decimal hours (e.g., 5.07) into "Xh Ym" format
  const formatDecimalHoursToTime = (value) => {
    if (value === undefined || value === null || value === "") return null;

    const decimalHours = parseFloat(value);
    if (isNaN(decimalHours) || decimalHours < 0) return null;

    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (minutes === 60) {
      return `${hours + 1}h 0m`;
    }

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const Tooltip = ({ children, content }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
      <div
        className="relative inline-flex items-center"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}

        {isVisible && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-6 z-[9999]">
            <div className="relative bg-gray-900 text-white text-xs rounded-lg shadow-lg py-2 pl-4 pr-3 min-w-[260px]">
              {content}

              {/* Arrow */}
              <div className="absolute right-full top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-gray-900 rotate-45"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSearchChange = (e) => {
    const keyword = e.target.value.toLowerCase();
    setSearchKeyword(keyword);

    if (keyword.trim() === "") {
      setFilteredBatches(batches || []);
    } else {
      const filtered = (batches || []).filter(
        (batch) =>
          batch.batch_no.toString().toLowerCase().includes(keyword) ||
          batch.courseName.toLowerCase().includes(keyword),
      );
      setFilteredBatches(filtered);
    }
  };

  const handleBatchSelect = (e) => {
    const batchNo = e.target.value;
    const batch = (batches || []).find((b) => b.batch_no === batchNo);
    setSelectedBatch(batch);
  };

  // Validation function for numeric fields
  const validateNumericFields = () => {
    const numericFields = {
      numOfStudents: "Number of Students",
      totalAmount: "Amount",
    };

    // Add conditional numeric fields for training sessions
    if (formData.sessionType === "Training") {
      numericFields.numOfClasses = "Number of Classes";
      numericFields.trainerLeaves = "Trainer Leaves";
    }

    for (const [field, label] of Object.entries(numericFields)) {
      const value = formData[field];
      if (value && (isNaN(value) || parseFloat(value) < 0)) {
        return `${label} must be a valid positive number`;
      }
    }

    // Special validation for totalAmount - must be greater than 0
    if (formData.totalAmount && parseFloat(formData.totalAmount) <= 0) {
      return "Amount must be greater than 0";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Dynamic required fields validation
    const baseRequiredFields = [
      "name",
      "email",
      "pan",
      "sessionType",
      "paymentType",
      "totalAmount",
      // Bank Details and UPI are mandatory — a trainer cannot raise an
      // invoice without providing both.
      "bankAccountHolderName",
      "bankAccountNumber",
      "bankIFSC",
      "upiDetails",
    ];

    let requiredFields = [...baseRequiredFields];

    if (formData.sessionType === "Training") {
      requiredFields.push("batchNo", "courseName");
    } else {
      requiredFields.push("sessionDate", "sessionTime", "numOfStudents");
    }

    for (let field of requiredFields) {
      if (
        !formData[field] ||
        (formData.sessionType === "Training" &&
          field === "batchNo" &&
          formData[field] === "")
      ) {
        setError(
          `Missing required field: ${field
            .replace(/([A-Z])/g, " $1")
            .toLowerCase()}`,
        );
        setLoading(false);
        return;
      }
    }

    // Validate numeric fields
    const numericValidationError = validateNumericFields();
    if (numericValidationError) {
      setError(numericValidationError);
      setLoading(false);
      return;
    }

    try {
      // NEW: Prepare student data for training sessions
      let studentNames = [];
      let numOfClassesArray = [];

      if (
        formData.sessionType === "Training" &&
        selectedBatch &&
        selectedBatch.students
      ) {
        studentNames = selectedBatch.students.map((student) => student.name);
        // `_sc` is studentClasses, sent under this key by the batches API
        // (see trainer.controller.js) so it isn't identifiable in the
        // network response.
        numOfClassesArray = selectedBatch.students.map(
          (student) => parseInt(student._sc) || 0,
        );
      }

      // Combine the split bank fields into a single formatted string for
      // backend compatibility, while still sending the individual fields.
      const bankDetails = `Account Holder Name: ${formData.bankAccountHolderName}\nAccount Number: ${formData.bankAccountNumber}\nIFSC Code: ${formData.bankIFSC}`;

      // Prepare data with correct field mapping and convert strings to numbers
      const submitData = {
        ...formData,
        // Convert numeric fields from strings to numbers
        numOfStudents: parseInt(formData.numOfStudents) || 0,
        numOfClasses: parseInt(formData.numOfClasses) || 0,
        numOfLeaves: parseInt(formData.trainerLeaves) || 0, // Map trainerLeaves to numOfLeaves for backend
        totalAmount: parseFloat(formData.totalAmount) || 0,
        // Also convert these fields if they exist
        numClassesAsMOU: parseInt(formData.numClassesAsMOU) || 0,
        costAsPerMOU: parseFloat(formData.costAsPerMOU) || 0,
        classes: parseFloat(formData.attendedHours) || 0,
        // Backend field mapping
        leaves: parseInt(formData.trainerLeaves) || 0,

        instructor_contact: formData.contactNumber || "",
        batchNo: formData.batchNo || "0",
        batchNumber2:
          formData.batchNumber2 === "0"
            ? 0
            : parseInt(formData.batchNumber2) || 0,
        batchNumber3:
          formData.batchNumber3 === "0"
            ? 0
            : parseInt(formData.batchNumber3) || 0,

        // Both Bank Details and UPI are always required and always sent.
        bankDetails,
        bankAccountHolderName: formData.bankAccountHolderName,
        bankAccountNumber: formData.bankAccountNumber,
        bankIFSC: formData.bankIFSC,
        upiDetails: formData.upiDetails,

        // NEW: Include student data for Training sessions
        ...(studentNames.length > 0 && { studentNames }),
        ...(numOfClassesArray.length > 0 && {
          numOfClasses: numOfClassesArray,
        }),
      };

      // console.log('Final submit data:', submitData);

      await TrainerAPI.raiseTrainerInvoice(submitData);

      toast.success("Invoice Submitted Successfully", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const isTrainingSession = formData.sessionType === "Training";
  const isGuestSession = formData.sessionType === "Demo/Interview/Guest";

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center min-h-screen justify-center">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Raise Invoice</h2>
          <button
            onClick={onClose}
            className="text-gray-500 text-xl hover:text-gray-700"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="text-red-600 text-sm mb-3 bg-red-50 border border-red-300 rounded px-3 py-2">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Session Type - First Field */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Session Type *
            </label>
            <div className="flex gap-4 flex-wrap">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="sessionType"
                  value="Demo/Interview/Guest"
                  checked={formData.sessionType === "Demo/Interview/Guest"}
                  onChange={handleChange}
                />
                <span className="text-sm">Demo/Interview/Guest</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="sessionType"
                  value="Training"
                  checked={formData.sessionType === "Training"}
                  onChange={handleChange}
                />
                <span className="text-sm">Training</span>
              </label>
            </div>
          </div>

          {/* Batch Selection - Only for Training */}
          {isTrainingSession && (
            <div className="relative">
              <label className="block text-sm font-medium mb-1">
                Select Batch *
              </label>

              {/* Searchable combined input */}
              <input
                type="text"
                placeholder="Search batch by number or course name"
                value={searchKeyword}
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                  setShowDropdown(true);
                  const keyword = e.target.value.toLowerCase();
                  if (keyword.trim() === "") {
                    setFilteredBatches(batches || []);
                  } else {
                    const filtered = (batches || []).filter(
                      (batch) =>
                        batch.batch_no
                          .toString()
                          .toLowerCase()
                          .includes(keyword) ||
                        batch.courseName.toLowerCase().includes(keyword),
                    );
                    setFilteredBatches(filtered);
                  }
                }}
                onFocus={() => setShowDropdown(true)}
                className="w-full border rounded-md px-3 py-2"
                required
              />

              {/* Dropdown menu */}
              {showDropdown && (
                <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 max-h-60 overflow-y-auto shadow-md">
                  {filteredBatches.length > 0 ? (
                    filteredBatches.map((batch) => (
                      <li
                        key={batch.batch_no}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setSelectedBatch(batch);
                          setSearchKeyword(
                            `Batch #${batch.batch_no} - ${batch.courseName}`,
                          );
                          setShowDropdown(false);
                        }}
                      >
                        Batch #{batch.batch_no} - {batch.courseName} (
                        {batch.students?.length || 0} students)
                      </li>
                    ))
                  ) : (
                    <li className="px-4 py-2 text-gray-500">
                      No batches found
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                PAN Number *
              </label>
              <input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Payment Type *
              </label>
              <select
                name="paymentType"
                value={formData.paymentType}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                required
              >
                <option value="">Select payment type...</option>
                <option value="partial">Partial Payment</option>
                <option value="full">Full Payment</option>
                <option value="prorata">Prorata</option>
              </select>
            </div>
          </div>

          {/* Session-specific fields */}
          {isGuestSession && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Date of Session *
                  </label>
                  <input
                    type="date"
                    name="sessionDate"
                    value={formData.sessionDate}
                    onChange={handleChange}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Time of Session (IST) *
                  </label>
                  <input
                    type="time"
                    name="sessionTime"
                    value={formData.sessionTime}
                    onChange={handleChange}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Number of Students *
                  </label>
                  <input
                    type="number"
                    name="numOfStudents"
                    value={formData.numOfStudents}
                    onChange={handleChange}
                    className="w-full border rounded-md px-3 py-2"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Batch Number
                  </label>
                  <input
                    type="text"
                    name="batchNo"
                    value={formData.batchNo}
                    readOnly
                    className="w-full border rounded-md px-3 py-2 bg-gray-100"
                    title="Default batch number for guest sessions"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Course/Topic Name
                </label>
                <input
                  type="text"
                  name="courseName"
                  value={formData.courseName}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
            </>
          )}

          {/* Training-specific readonly fields */}
          {isTrainingSession &&
            selectedBatch &&
            selectedBatch.students &&
            selectedBatch.students.length > 0 && (
              <>
                <div>
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">
                      Student Details
                    </h3>
                    <div className="max-h-40 overflow-y-auto">
                      <table className="w-full table-auto text-sm border border-gray-200 rounded">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 border-r">#</th>
                            <th className="text-left px-3 py-2 border-r">
                              Student Name
                            </th>
                            <th className="text-left px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBatch.students.map((student, idx) => (
                            <tr
                              key={idx}
                              className="border-t border-gray-200 hover:bg-gray-50"
                            >
                              <td className="px-3 py-2 border-r text-center">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2 border-r">
                                {student.name}
                              </td>
                              <td className="px-3 py-2">
                                {student.studentStatus ? (
                                  <span
                                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStudentStatusColor(student.studentStatus)}`}
                                  >
                                    {student.studentStatus}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">N/A</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <label className="block text-sm font-medium mb-1">
                    Course Name
                  </label>
                  <input
                    type="text"
                    name="courseName"
                    value={formData.courseName}
                    readOnly
                    className="w-full border rounded-md px-3 py-2 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Batch Timing
                  </label>
                  <input
                    type="text"
                    name="batchTiming"
                    value={formData.batchTiming}
                    readOnly
                    className="w-full border rounded-md px-3 py-2 bg-gray-100"
                  />
                </div>

                {/* MOU Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Classes As Per MOU
                    </label>
                    <input
                      type="text"
                      name="numClassesAsMOU"
                      value={formData.numClassesAsMOU}
                      readOnly
                      className="w-full border rounded-md px-3 py-2 bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      MOU Amount (Per Student)
                    </label>
                    <input
                      type="text"
                      name="costAsPerMOU"
                      value={formData.costAsPerMOU}
                      readOnly
                      className="w-full border rounded-md px-3 py-2 bg-gray-100"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Attended Hours
                  </label>
                  <input
                    type="text"
                    name="attendedHours"
                    value={formatDecimalHoursToTime(formData.attendedHours) || ""}
                    readOnly
                    className="w-full border rounded-md px-3 py-2 bg-gray-100"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Students
                    </label>
                    <input
                      type="number"
                      name="numOfStudents"
                      value={formData.numOfStudents}
                      readOnly
                      className="w-full border rounded-md px-3 py-2 bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Leaves
                    </label>
                    <input
                      type="number"
                      name="trainerLeaves"
                      value={formData.trainerLeaves}
                      readOnly
                      className="w-full border rounded-md px-3 py-2 bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Amount *
                    </label>
                    <input
                      type="number"
                      name="totalAmount"
                      value={formData.totalAmount}
                      onChange={handleChange}
                      className="w-full border rounded-md px-3 py-2"
                      min="1"
                      required
                    />
                  </div>
                </div>
              </>
            )}

          {/* Amount field for guest sessions */}
          {isGuestSession && (
            <div>
              <label className="block text-sm font-medium mb-1">Amount *</label>
              <input
                type="number"
                name="totalAmount"
                value={formData.totalAmount}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                min="1"
                required
              />
            </div>
          )}

          {/* Bank Details — split fields, all mandatory */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              Bank Details *
              <Tooltip content="Bank Details are required to raise an invoice.">
                <Info className="w-4 h-4 text-gray-400 hover:text-blue-600 cursor-help transition-colors" />
              </Tooltip>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">
                  Account Holder Name *
                </label>
                <input
                  type="text"
                  name="bankAccountHolderName"
                  value={formData.bankAccountHolderName}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="Account Holder Name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">
                  Account Number *
                </label>
                <input
                  type="text"
                  name="bankAccountNumber"
                  value={formData.bankAccountNumber}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="Account Number"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">
                  IFSC Code *
                </label>
                <input
                  type="text"
                  name="bankIFSC"
                  value={formData.bankIFSC}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="IFSC Code"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Contact Number
              </label>
              <input
                type="text"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                placeholder="Contact Number"
                readOnly
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-1">
                UPI ID *
                <Tooltip content="UPI ID is required to raise an invoice.">
                  <Info className="w-4 h-4 text-gray-400 hover:text-blue-600 cursor-help transition-colors" />
                </Tooltip>
              </label>
              <input
                type="text"
                name="upiDetails"
                value={formData.upiDetails}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                placeholder="yourname@upi"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Remark</label>
            <textarea
              name="remark"
              value={formData.remark}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
              rows="2"
              placeholder="Additional notes or remarks"
            />
          </div>


          <div>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1"
              />

              <span className="text-sm text-gray-600 leading-relaxed font-medium tracking-wide">
                I hereby certify that the invoice amount and selected payment details are true, accurate, and complete.
              </span>
            </label>
          </div>



          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>

            <span
              title={
                !acknowledged
                  ? "Please check the acknowledgment box to enable submission"
                  : ""
              }
            >
              <button
                type="submit"
                disabled={loading || !acknowledged}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Submitting..." : "Submit Invoice"}
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RaiseInvoiceModal;